import React, { useMemo, useState } from "react";
import { authFetch } from "../../api/api";
import Button from "../../UI/Button";
import Modal from "../../UI/Modal";
import styles from "./OrderFinances.module.css";

const BASE_URL = process.env.REACT_APP_DJANGO_BACKEND;
const ACCOUNTING_API = `${BASE_URL}/api/accounting/accounting-records/`;

const formatMoney = (value) =>
  Number(value || 0).toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("ru-RU");
};

const getErrorMessage = async (response, fallback) => {
  try {
    const data = await response.json();

    if (typeof data === "string") return data;
    if (data?.error) {
      return typeof data.error === "string"
        ? data.error
        : JSON.stringify(data.error, null, 2);
    }
    if (data?.detail) {
      return typeof data.detail === "string"
        ? data.detail
        : JSON.stringify(data.detail, null, 2);
    }

    return (
      Object.entries(data || {})
        .map(([key, value]) => {
          const message =
            typeof value === "string"
              ? value
              : JSON.stringify(value, null, 2);
          return `${key}: ${message}`;
        })
        .join("\n") || fallback
    );
  } catch {
    return fallback;
  }
};

const getOurUnallocated = (allocation) => {
  if (allocation.our_unallocated_sum != null) {
    return Number(allocation.our_unallocated_sum || 0);
  }

  const allocated = (allocation.our_allocations || []).reduce(
    (sum, item) => sum + Number(item.amount || 0),
    0
  );

  return Math.max(0, Number(allocation.amount || 0) - allocated);
};

const uploadOurAllocationDocuments = async (ourAllocationId, files) => {
  const formData = new FormData();

  files.forEach((file) => {
    formData.append("files", file);
  });

  const response = await authFetch(
    `${BASE_URL}/api/accounting/our-allocations/${ourAllocationId}/documents/`,
    {
      method: "POST",
      body: formData,
    }
  );

  if (!response.ok) {
    throw new Error(
      await getErrorMessage(
        response,
        "Не удалось загрузить подтверждающие документы."
      )
    );
  }

  return response;
};

export default function OurPaymentToFactoryModal({
  isOpen,
  onClose,
  orderId,
  accountingRecords = [],
  onSaved,
}) {
  const [selected, setSelected] = useState(null);
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState("");
  const [comment, setComment] = useState("");
  const [proofFiles, setProofFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [pendingUploadOurAllocationId, setPendingUploadOurAllocationId] =
    useState(null);

  // Собираем ВСЕ клиентские allocations из всех Accounting-записей,
  // у которых есть ненулевой остаток our_unallocated_sum.
  const availableAllocations = useMemo(() => {
    const result = [];

    accountingRecords.forEach((record) => {
      (record.allocations || []).forEach((allocation) => {
        const remaining = getOurUnallocated(allocation);

        if (remaining > 0.005) {
          result.push({
            key: `rec-${record.id}-alloc-${allocation.id}`,
            recordId: record.id,
            allocationId: allocation.id,
            clientId: record.client,
            clientName: record.client_name,
            orderCode:
              allocation.order_code ??
              allocation.order?.code ??
              allocation.order ??
              "—",
            clientAllocationAmount: Number(allocation.amount || 0),
            ourUnallocatedSum: remaining,
            date: allocation.date || record.date,
            comment: allocation.comment || record.comment || "",
          });
        }
      });
    });

    return result.sort((a, b) => {
      const dateA = a.date ? new Date(a.date).getTime() : 0;
      const dateB = b.date ? new Date(b.date).getTime() : 0;
      return dateB - dateA;
    });
  }, [accountingRecords]);

  const openForm = (item) => {
    setSelected(item);
    setAmount(item.ourUnallocatedSum.toFixed(2));
    setDate(new Date().toISOString().slice(0, 10));
    setComment("");
    setProofFiles([]);
    setPendingUploadOurAllocationId(null);
    setError("");
  };

  const closeForm = () => {
    if (saving) return;

    setSelected(null);
    setAmount("");
    setDate("");
    setComment("");
    setProofFiles([]);
    setPendingUploadOurAllocationId(null);
    setError("");
  };

  const closeAll = () => {
    if (saving) return;
    closeForm();
    onClose();
  };

  const save = async (event) => {
    event.preventDefault();

    if (!selected) return;

    // Повторная попытка загрузки документов после уже сохранённого платежа.
    if (pendingUploadOurAllocationId) {
      if (proofFiles.length === 0) {
        setError("Выберите документы для загрузки.");
        return;
      }

      setSaving(true);
      setError("");

      try {
        await uploadOurAllocationDocuments(
          pendingUploadOurAllocationId,
          proofFiles
        );

        setPendingUploadOurAllocationId(null);
        closeForm();
        onClose();

        if (onSaved) {
          await onSaved();
        }
      } catch (uploadError) {
        console.error(uploadError);
        setError(
          `Платёж уже сохранён, но загрузить документы не удалось. ${
            uploadError.message || "Попробуйте ещё раз."
          }`
        );
      } finally {
        setSaving(false);
      }

      return;
    }

    const numericAmount = Number(amount);

    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setError("Сумма должна быть больше нуля.");
      return;
    }

    if (numericAmount > selected.ourUnallocatedSum + 0.005) {
      setError(
        `Сумма превышает остаток нашего распределения: ¥${formatMoney(
          selected.ourUnallocatedSum
        )}.`
      );
      return;
    }

    setSaving(true);
    setError("");

    let createdOurAllocationId = null;
    let allocationWasSaved = false;

    try {
      const detailUrl = `${ACCOUNTING_API}${selected.recordId}/`;

      // Загружаем актуальную запись, чтобы не затереть чужие распределения.
      const detailResponse = await authFetch(detailUrl);

      if (!detailResponse.ok) {
        throw new Error(
          await getErrorMessage(
            detailResponse,
            "Не удалось загрузить платёж клиента."
          )
        );
      }

      const latestRecord = await detailResponse.json();
      const allocations = Array.isArray(latestRecord.allocations)
        ? latestRecord.allocations
        : [];

      const targetAllocation = allocations.find(
        (a) => Number(a.id) === Number(selected.allocationId)
      );

      if (!targetAllocation) {
        throw new Error("Клиентское распределение больше не существует.");
      }

      const latestOurUnallocated = getOurUnallocated(targetAllocation);

      if (numericAmount > latestOurUnallocated + 0.005) {
        throw new Error(
          `Остаток изменился. Сейчас доступно: ¥${formatMoney(
            latestOurUnallocated
          )}. Закройте модалку и откройте снова.`
        );
      }

      // Сохраняем существующие our_allocations без изменений и
      // добавляем новое our_allocation к выбранному клиентскому allocation.
      const updatedAllocations = allocations.map((allocation) => {
        const orderIdForAlloc = allocation.order?.id ?? allocation.order;

        const rebuiltOur = (allocation.our_allocations || []).map((item) => ({
          id: item.id,
          order: Number(item.order?.id ?? item.order),
          amount: String(item.amount),
          date: item.date || null,
          payment_agent: item.payment_agent || "",
          payment_status: item.payment_status || "",
          comment: item.comment || "",
        }));

        if (Number(allocation.id) === Number(selected.allocationId)) {
          rebuiltOur.push({
            order: Number(orderId),
            amount: numericAmount.toFixed(2),
            date: date || null,
            payment_agent: "",
            payment_status: "PENDING",
            comment: comment.trim(),
          });
        }

        return {
          id: allocation.id,
          order: Number(orderIdForAlloc),
          amount: String(allocation.amount),
          date: allocation.date || null,
          payment_agent: allocation.payment_agent || "",
          payment_status: allocation.payment_status || "",
          comment: allocation.comment || "",
          our_allocations: rebuiltOur,
        };
      });

      const saveResponse = await authFetch(detailUrl, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ allocations: updatedAllocations }),
      });

      if (!saveResponse.ok) {
        throw new Error(
          await getErrorMessage(
            saveResponse,
            "Не удалось сохранить наш платёж."
          )
        );
      }

      allocationWasSaved = true;

      // Если приложены документы — нужно найти id нового our_allocation.
      if (proofFiles.length > 0) {
        const existingOurIds = new Set();

        allocations.forEach((a) => {
          (a.our_allocations || []).forEach((oa) => {
            existingOurIds.add(String(oa.id));
          });
        });

        const findNewOur = (record) => {
          const list = record?.allocations || [];
          const target = list.find(
            (a) => Number(a.id) === Number(selected.allocationId)
          );

          if (!target) return null;

          return (target.our_allocations || []).find(
            (oa) => oa.id != null && !existingOurIds.has(String(oa.id))
          );
        };

        let updatedRecord = null;

        try {
          updatedRecord = await saveResponse.json();
        } catch {
          // ignore
        }

        let createdOur = findNewOur(updatedRecord);

        if (!createdOur?.id) {
          const refreshResponse = await authFetch(detailUrl);

          if (refreshResponse.ok) {
            const refreshed = await refreshResponse.json();
            createdOur = findNewOur(refreshed);
          }
        }

        if (!createdOur?.id) {
          throw new Error(
            "Платёж создан, но не удалось определить его ID для загрузки документов."
          );
        }

        createdOurAllocationId = createdOur.id;
        setPendingUploadOurAllocationId(createdOurAllocationId);

        await uploadOurAllocationDocuments(createdOurAllocationId, proofFiles);
        setPendingUploadOurAllocationId(null);
      }

      closeForm();
      onClose();

      if (onSaved) {
        await onSaved();
      }
    } catch (saveError) {
      console.error(saveError);

      if (createdOurAllocationId) {
        setPendingUploadOurAllocationId(createdOurAllocationId);
        setError(
          `Платёж сохранён, но документы не загрузились. ${
            saveError.message || "Попробуйте повторить загрузку."
          }`
        );
      } else if (allocationWasSaved) {
        setError(
          saveError.message ||
            "Платёж сохранён, но не удалось загрузить документы."
        );
      } else {
        setError(saveError.message || "Не удалось сохранить платёж.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={true}
      onClose={() => {
        if (selected) closeForm();
        else closeAll();
      }}
    >
      <div className="w-full min-w-[320px] max-w-3xl space-y-4 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold">Наш платёж фабрике</h2>
            <p className="mt-1 text-sm text-gray-600">
              Выберите клиентское назначение с нераспределённым остатком
              нашего платежа.
            </p>
          </div>

          <Button
            type="button"
            variant="secondary"
            disabled={saving}
            onClick={() => {
              if (selected) closeForm();
              else onClose();
            }}
          >
            Закрыть
          </Button>
        </div>

        {selected ? (
          <form onSubmit={save} className="space-y-4">
            <button
              type="button"
              className="text-sm text-blue-600 underline"
              disabled={saving}
              onClick={closeForm}
            >
              ← К списку
            </button>

            <div className="rounded-lg border bg-gray-50 p-4">
              <p className="font-semibold">
                Клиент: {selected.clientName || "—"}
              </p>
              <p className="mt-1 text-sm text-gray-600">
                Заказ клиента: {selected.orderCode || "—"}
              </p>
              <p className="text-sm text-gray-600">
                Дата назначения: {formatDate(selected.date)}
              </p>
              <p className="mt-2 text-sm">
                Сумма клиентского назначения: ¥
                {formatMoney(selected.clientAllocationAmount)}
              </p>
              <p className="font-semibold text-green-700">
                Доступно для нашего платежа: ¥
                {formatMoney(selected.ourUnallocatedSum)}
              </p>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">Сумма</label>
              <input
                type="number"
                onWheel={(e) => e.currentTarget.blur()}
                min="0.01"
                step="0.01"
                required
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className={`${styles.noNumberSpinner} w-full rounded border px-3 py-2`}
              />
              <button
                type="button"
                className="mt-1 text-sm text-blue-600 underline"
                onClick={() =>
                  setAmount(selected.ourUnallocatedSum.toFixed(2))
                }
              >
                Использовать весь остаток
              </button>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">Дата</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full rounded border px-3 py-2"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">
                Комментарий
              </label>
              <textarea
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="w-full rounded border px-3 py-2"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">
                Подтверждающий документ
              </label>
              <input
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
                disabled={saving || pendingUploadOurAllocationId !== null}
                onChange={(e) => {
                  setProofFiles(Array.from(e.target.files || []));
                  setError("");
                  e.target.value = "";
                }}
                className="block w-full rounded border px-3 py-2 text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                Можно выбрать несколько файлов: PDF, JPG, PNG или WEBP.
              </p>

              {proofFiles.length > 0 && (
                <div className="mt-2 rounded border bg-gray-50 p-3">
                  <p className="mb-2 text-sm font-medium">
                    Выбранные файлы ({proofFiles.length})
                  </p>

                  <ul className="space-y-2">
                    {proofFiles.map((file, index) => (
                      <li
                        key={`${file.name}-${file.lastModified}-${file.size}`}
                        className="flex items-center justify-between gap-3 text-sm"
                      >
                        <span className="min-w-0 break-all">
                          {file.name}
                          <span className="ml-2 text-gray-500">
                            ({(file.size / 1024 / 1024).toFixed(2)} МБ)
                          </span>
                        </span>

                        <button
                          type="button"
                          className="shrink-0 text-red-600 hover:underline disabled:text-gray-400"
                          disabled={
                            saving || pendingUploadOurAllocationId !== null
                          }
                          onClick={() =>
                            setProofFiles((current) =>
                              current.filter((_, i) => i !== index)
                            )
                          }
                        >
                          Удалить
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {error && (
              <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={saving}
                onClick={closeForm}
              >
                Отмена
              </Button>

              <Button type="submit" variant="primary" disabled={saving}>
                {saving
                  ? pendingUploadOurAllocationId
                    ? "Загрузка документов..."
                    : "Сохранение..."
                  : pendingUploadOurAllocationId
                  ? "Повторить загрузку документов"
                  : "Сохранить платёж"}
              </Button>
            </div>
          </form>
        ) : (
          <div className="max-h-[65vh] space-y-3 overflow-y-auto">
            {availableAllocations.length === 0 ? (
              <div className="rounded-lg border border-dashed p-6 text-center text-gray-500">
                Нет клиентских назначений с нераспределённым остатком нашего
                платежа.
              </div>
            ) : (
              availableAllocations.map((item) => (
                <div
                  key={item.key}
                  className="rounded-lg border bg-white p-4 shadow-sm"
                >
                  <div className="flex flex-col justify-between gap-3 sm:flex-row">
                    <div className="space-y-1">
                      <p className="font-semibold">
                        Клиент: {item.clientName || "—"}
                      </p>
                      <p className="text-sm text-gray-600">
                        Заказ клиента: {item.orderCode || "—"}
                      </p>
                      <p className="text-sm text-gray-600">
                        Дата: {formatDate(item.date)}
                      </p>
                      {item.comment && (
                        <p className="whitespace-pre-wrap text-sm text-gray-600">
                          {item.comment}
                        </p>
                      )}
                    </div>

                    <div className="min-w-[200px] space-y-1 sm:text-right">
                      <p className="text-sm">
                        Сумма назначения: ¥
                        {formatMoney(item.clientAllocationAmount)}
                      </p>
                      <p className="font-bold text-green-700">
                        Остаток: ¥{formatMoney(item.ourUnallocatedSum)}
                      </p>
                      <Button
                        type="button"
                        variant="primary"
                        onClick={() => openForm(item)}
                      >
                        Выбрать
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
